export interface ScreenLayoutPreset {
  template: 'start-stack' | 'entry-stack' | 'control-stack' | 'analysis-stack' | 'play-stack' | 'split-start' | 'split-control' | 'split-analysis' | 'split-reference';
  density: 'compact' | 'comfortable' | 'immersive';
  contentWidth: 'layout' | 'md' | 'lg' | '3xl' | '5xl';
}

export interface LayoutDefinition {
  id: string;
  name: string;
  description?: string;
  preview: string[];
  cssVars: Record<`--${string}`, string>;
  screens: {
    home: ScreenLayoutPreset;
    lobby: ScreenLayoutPreset;
    addPlayers: ScreenLayoutPreset;
    setup: ScreenLayoutPreset;
    game: ScreenLayoutPreset;
    host: ScreenLayoutPreset;
    join: ScreenLayoutPreset;
    gameOver: ScreenLayoutPreset;
    gameResults: ScreenLayoutPreset;
    scores: ScreenLayoutPreset;
    stats: ScreenLayoutPreset;
    guide: ScreenLayoutPreset;
    legal: ScreenLayoutPreset;
  };
}

export type LayoutId = LayoutDefinition['id'];
export type LayoutScreenKey = keyof LayoutDefinition['screens'];
type ScreenTemplate = ScreenLayoutPreset['template'];
export const DESKTOP_LAYOUT_MEDIA_QUERY = '(min-width: 1024px)';

const layoutsInternal: LayoutDefinition[] = [
  {
    id: 'mobile',
    name: 'Mobile',
    description: 'Jednokolumnowy układ na telefon z kompaktowymi kartami i dolnym paskiem akcji.',
    preview: ['#0A3A52', '#0E4D64', '#FFB703'],
    cssVars: {
      '--layout-content-max-width': '28rem',
      '--layout-floating-controls-max-width': '32rem',
      '--layout-screen-padding-x': '0.5rem',
      '--top-gap': '6px',
    },
    screens: {
      home: { template: 'start-stack', density: 'comfortable', contentWidth: 'layout' },
      lobby: { template: 'entry-stack', density: 'comfortable', contentWidth: 'layout' },
      addPlayers: { template: 'entry-stack', density: 'comfortable', contentWidth: 'layout' },
      setup: { template: 'control-stack', density: 'comfortable', contentWidth: 'layout' },
      game: { template: 'play-stack', density: 'compact', contentWidth: 'lg' },
      host: { template: 'play-stack', density: 'compact', contentWidth: 'lg' },
      join: { template: 'entry-stack', density: 'comfortable', contentWidth: '3xl' },
      gameOver: { template: 'control-stack', density: 'comfortable', contentWidth: 'layout' },
      gameResults: { template: 'analysis-stack', density: 'comfortable', contentWidth: '5xl' },
      scores: { template: 'analysis-stack', density: 'comfortable', contentWidth: '5xl' },
      stats: { template: 'analysis-stack', density: 'comfortable', contentWidth: '5xl' },
      guide: { template: 'analysis-stack', density: 'comfortable', contentWidth: '3xl' },
      legal: { template: 'analysis-stack', density: 'comfortable', contentWidth: '3xl' },
    },
  },
  {
    id: 'desktop',
    name: 'Desktop',
    description: 'Szeroki układ z bocznymi panelami i odmiennym stylem kart oraz przyciskow.',
    preview: ['#082F43', '#0F5C73', '#FFB703'],
    cssVars: {
      '--layout-content-max-width': '78rem',
      '--layout-floating-controls-max-width': '76rem',
      '--layout-screen-padding-x': '1rem',
      '--top-gap': '10px',
    },
    screens: {
      home: { template: 'split-start', density: 'comfortable', contentWidth: 'layout' },
      lobby: { template: 'split-control', density: 'comfortable', contentWidth: 'layout' },
      addPlayers: { template: 'split-control', density: 'comfortable', contentWidth: 'layout' },
      setup: { template: 'split-control', density: 'comfortable', contentWidth: 'layout' },
      game: { template: 'play-stack', density: 'immersive', contentWidth: 'layout' },
      host: { template: 'play-stack', density: 'immersive', contentWidth: 'layout' },
      join: { template: 'entry-stack', density: 'comfortable', contentWidth: 'layout' },
      gameOver: { template: 'control-stack', density: 'comfortable', contentWidth: 'layout' },
      gameResults: { template: 'split-analysis', density: 'comfortable', contentWidth: '5xl' },
      scores: { template: 'split-analysis', density: 'comfortable', contentWidth: 'layout' },
      stats: { template: 'split-analysis', density: 'comfortable', contentWidth: '5xl' },
      guide: { template: 'split-reference', density: 'comfortable', contentWidth: '5xl' },
      legal: { template: 'split-reference', density: 'comfortable', contentWidth: '5xl' },
    },
  },
] as const;

export const DEFAULT_LAYOUT_ID: LayoutId = 'mobile';

const layoutMap = new Map<LayoutId, LayoutDefinition>(layoutsInternal.map((layout) => [layout.id, layout]));
const ALL_LAYOUT_VARS = Array.from(
  layoutsInternal.reduce((acc, layout) => {
    Object.keys(layout.cssVars).forEach((key) => acc.add(key));
    return acc;
  }, new Set<string>()),
);

function matchesDesktopLayout(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(DESKTOP_LAYOUT_MEDIA_QUERY).matches;
}

export function getAutoLayoutId(): LayoutId {
  return matchesDesktopLayout() ? 'desktop' : 'mobile';
}

export function readForcedLayoutId(): LayoutId | null {
  if (typeof window === 'undefined') return null;
  const candidate = (window as Window & { __DICE_LAYOUT_OVERRIDE__?: unknown }).__DICE_LAYOUT_OVERRIDE__;
  return isValidLayoutId(candidate) ? candidate : null;
}

function normalizeLayoutId(id: unknown): string | null {
  if (typeof id !== 'string') return null;
  return layoutMap.has(id as LayoutId) ? id : null;
}

export function isValidLayoutId(id: unknown): id is LayoutId {
  const normalized = normalizeLayoutId(id);
  return typeof normalized === 'string' && layoutMap.has(normalized as LayoutId);
}

export function getLayout(id: string | null | undefined): LayoutDefinition {
  const normalized = normalizeLayoutId(id);
  if (normalized && layoutMap.has(normalized as LayoutId)) {
    return layoutMap.get(normalized as LayoutId)!;
  }
  return layoutMap.get(DEFAULT_LAYOUT_ID)!;
}

export function applyLayout(layoutId: string): void {
  if (typeof document === 'undefined') return;
  const layout = getLayout(layoutId);
  const root = document.documentElement;
  root.dataset.layout = layout.id;
  ALL_LAYOUT_VARS.forEach((name) => root.style.removeProperty(name));
  Object.entries(layout.cssVars).forEach(([name, value]) => {
    root.style.setProperty(name, value);
  });
}

export function getScreenLayout(layoutId: string | null | undefined, screenKey: LayoutScreenKey): ScreenLayoutPreset {
  return getLayout(layoutId).screens[screenKey];
}

function isScreenTemplate(screenLayout: ScreenLayoutPreset, template: ScreenTemplate): boolean {
  return screenLayout.template === template;
}

export function isSplitStartLayout(screenLayout: ScreenLayoutPreset): boolean {
  return isScreenTemplate(screenLayout, 'split-start');
}

export function isSplitControlLayout(screenLayout: ScreenLayoutPreset): boolean {
  return isScreenTemplate(screenLayout, 'split-control');
}
export function isSplitReferenceLayout(screenLayout: ScreenLayoutPreset): boolean {
  return isScreenTemplate(screenLayout, 'split-reference');
}

export function resolveContentWidthClass(contentWidth: ScreenLayoutPreset['contentWidth']): string | undefined {
  switch (contentWidth) {
    case 'md':
      return 'max-w-md';
    case 'lg':
      return 'max-w-lg';
    case '3xl':
      return 'max-w-3xl';
    case '5xl':
      return 'max-w-5xl';
    case 'layout':
    default:
      return undefined;
  }
}
